<div class="w-full h-full overflow-hidden grid grid-cols-2 gap-4">

    <article
        class="col-span-1 divide-y divide-zinc-200 dark:divide-zinc-600 overflow-hidden rounded-lg bg-white dark:bg-gray-800 card-container-pale">
        <div class="px-2 pb-3">
            <div class="flex items-center gap-2">
                <h3 class="text-sm font-semibold text-primary">Comment Sentiment</h3>
            </div>

            <p class="mt-1 text-xs text-secondary">How did the audience respond to the content?</p>
        </div>

        <div class="px-2 py-3 flex flex-col gap-y-4">
            @if (totalComments() && totalComments()! > 0) {
            <div class="mx-auto flex max-w-xs flex-col gap-y-0">
                <dt class="text-base/7 text-primary text-center">Overall Sentiment Score</dt>
                <dd
                    class="order-first text-3xl font-semibold tracking-tight text-accent text-center">
                    {{ weightedAverage() | number: '1.1-1' }} / 10</dd>
            </div>

            @for (sentiment of sentimentPercentages(); track $index) {
            <div
                class="flex flex-row w-full items-center justify-between bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                <div class="flex flex-row items-center gap-x-2">
                    @switch (sentiment.icon) {
                    @case ('thumbs-up') {
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="lucide lucide-thumbs-up-icon lucide-thumbs-up size-5" [class]="sentiment.color">
                        <path d="M7 10v12" />
                        <path
                            d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
                    </svg>
                    }
                    @case ('expressionless') {
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="lucide lucide-meh-icon lucide-meh size-5" [class]="sentiment.color">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="8" x2="16" y1="15" y2="15" />
                        <line x1="9" x2="9.01" y1="9" y2="9" />
                        <line x1="15" x2="15.01" y1="9" y2="9" />
                    </svg>
                    }
                    @case ('thumbs-down') {
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="lucide lucide-thumbs-down-icon lucide-thumbs-down size-5" [class]="sentiment.color">
                        <path d="M17 14V2" />
                        <path
                            d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />
                    </svg>
                    }
                    }
                    <span class="text-md text-gray-600 dark:text-gray-300">{{ sentiment.label }}</span>
                </div>
                <span class="text-md font-semibold" [class]="sentiment.color">{{
                    ((getSentimentPercentage(sentiment.label) / totalComments()!) * 100) | number: '1.0-0' }}%</span>
            </div>
            }

            <div
                class="border-l-4 border-primary dark:border-primary bg-primary/10 dark:bg-primary/15 p-4 rounded-r-lg">
                <div class="flex">
                    <div class="shrink-0">
                        <svg viewBox="0 0 20 20" fill="currentColor" data-slot="icon" aria-hidden="true"
                            class="size-5 text-accent">
                            <path
                                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                                clip-rule="evenodd" fill-rule="evenodd" />
                        </svg>
                    </div>
                    <div class="ml-3 flex-1 flex flex-row justify-between items-center">
                        <p class="text-sm text-accent/90">
                            Total comments analyzed
                        </p>
                        <p class="font-medium text-accent/90">{{ totalComments()! | number:
                            '1.0-0' }}</p>
                    </div>
                </div>
            </div>
            }
        </div>
    </article>


    <article
        class="col-span-1 divide-y divide-zinc-200 dark:divide-zinc-600 overflow-hidden rounded-lg bg-white dark:bg-gray-800 shadow card-container-pale flex flex-col">
        <div class="px-2 pb-3 shrink-0">
            <div class="flex items-center gap-2">
                <h3 class="text-sm font-semibold text-primary">Most Well-Received Posts</h3>
            </div>

            <p class="mt-1 text-xs text-secondary">Which creators received the most positive feedback?</p>
        </div>

        <div class="px-2 py-3 flex flex-col gap-y-4 overflow-y-auto scrollbar-transparent flex-1 min-h-0">
            @if (topPerformers() && topPerformers()!.length > 0) {
            @for (performer of topPerformers(); track $index) {
            <div class="flex flex-col w-full items-start bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2 gap-y-2">
                <div class="flex flex-row items-center gap-x-2 justify-between w-full">
                    <span class="text-lg font-semibold text-accent">{{ performer.handle }}</span>
                    <span
                        class="inline-flex items-center rounded-md bg-green-50 dark:bg-green-900/30 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-300 ring-1 ring-inset ring-green-600/20 dark:ring-green-500/30">{{
                        performer.score }}/10</span>
                </div>

                <p class="text-sm text-gray-600 dark:text-gray-300">{{ performer.caption }}</p>

                <div class="flex flex-row items-center justify-between w-full">
                    <div class="flex flex-row items-center gap-x-4">
                        <span class="inline-flex items-center gap-x-1.5 text-sm text-gray-600 dark:text-gray-300">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="lucide lucide-heart-icon lucide-heart size-4">
                                <path
                                    d="M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5" />
                            </svg>
                            {{ formatService.convertIntToString(performer.likes) }}
                        </span>
                        <span class="inline-flex items-center gap-x-1.5 text-sm text-gray-600 dark:text-gray-300">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round"
                                class="lucide lucide-message-circle-icon lucide-message-circle size-4">
                                <path
                                    d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719" />
                            </svg>
                            {{ formatService.convertIntToString(performer.comments) }}
                        </span>
                        <span class="inline-flex items-center gap-x-1.5 text-sm text-gray-600 dark:text-gray-300">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="lucide lucide-share2-icon lucide-share-2 size-4">
                                <circle cx="18" cy="5" r="3" />
                                <circle cx="6" cy="12" r="3" />
                                <circle cx="18" cy="19" r="3" />
                                <line x1="8.59" x2="15.42" y1="13.51" y2="17.49" />
                                <line x1="15.41" x2="8.59" y1="6.51" y2="10.49" />
                            </svg>
                            {{ formatService.convertIntToString(performer.shares) }}
                        </span>
                    </div>

                    <div class="flex flex-row items-center gap-x-2">
                        <!-- <button type="button" (click)="openPostInNewTab(performer.videoLink)" -->
                        <button type="button" class="justify-end text-gray-900 dark:text-gray-100 hover:cursor-pointer">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="currentColor" class="size-5 text-gray-700 dark:text-gray-300">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                            </svg>
                        </button>

                        <!-- <button type="button" (click)="launchContentViewer(performer)" -->
                        <button type="button" class="justify-end text-gray-900 dark:text-gray-100 hover:cursor-pointer">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
                                class="size-5 text-gray-700 dark:text-gray-300">
                                <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                                <path fill-rule="evenodd"
                                    d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 0 1 0-1.113ZM17.25 12a5.25 5.25 0 1 1-10.5 0 5.25 5.25 0 0 1 10.5 0Z"
                                    clip-rule="evenodd" />
                            </svg>
                        </button>
                    </div>

                </div>
            </div>
            }
            }
        </div>
    </article>

</div>